import type * as React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationChangeRequestsSideSheet } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    SidePanel,
    SidePanelBody,
    SidePanelBodyContent,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import {
    ChangeRequestsList,
    ChangeRequestsListEmptyState,
    ChangeRequestsListFilter,
    type ChangeRequestsListFilterState,
    useCreateChangeRequest,
} from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

/**
 * Sidepanel to display the change requests of a space.
 */
export function ChangeRequestsListSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}): React.ReactElement | null {
    const { space, analyticsContext } = props;
    const { close, sideSheet, updateSideSheet } =
        useSideSheetNavigation<NavigationChangeRequestsSideSheet>('change-requests');

    const filters = sideSheet?.filters ?? {};
    const onFiltersChange = (filters: ChangeRequestsListFilterState) => {
        updateSideSheet({ type: 'change-requests', filters });
    };

    useSetAnalyticsScreen(
        { ...analyticsContext, screen: 'space_change_requests', screenTab: filters.status },
        [filters.status]
    );

    return (
        <SidePanel onClose={close}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Change requests</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                <SidePanelHeaderSecondary>
                    <ChangeRequestsListFilter
                        organizationId={space.organization}
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        size="small"
                        withSpaceFilter={false}
                    />
                </SidePanelHeaderSecondary>
            </SidePanelHeader>
            <SidePanelBody>
                <SidePanelBodyContent className="flex-1 py-3">
                    <ChangeRequestsList
                        organizationId={space.organization}
                        spaceId={space.id}
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        emptyState={
                            filters?.contributor ||
                            filters?.creator ||
                            filters?.requestedReview ||
                            filters?.conversationsIssuesCluster ||
                            filters?.status === api.ChangeRequestStatus.Archived ||
                            filters?.status === api.ChangeRequestStatus.Merged ? (
                                <ChangeRequestsListEmptyState
                                    filters={filters}
                                    onFiltersChange={onFiltersChange}
                                />
                            ) : (
                                <ChangeRequestsStartEmptyState
                                    space={space}
                                    analyticsContext={analyticsContext}
                                />
                            )
                        }
                    />
                </SidePanelBodyContent>
            </SidePanelBody>
        </SidePanel>
    );
}

/**
 * Empty state to encourage the user to start a change request.
 */
function ChangeRequestsStartEmptyState(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { space } = props;

    const createChangeRequest = useCreateChangeRequest();

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.ChangeRequest />
            </EmptyStateIcon>
            <Heading>No change requests yet</Heading>
            <Description>Start making changes to your docs with a change request.</Description>
            {space.permissions.edit ? (
                <EmptyStateActions>
                    <Button
                        kind="primary"
                        leadingIcon={Icon.ChangeRequestNew}
                        label="New change request"
                        onPress={async () => {
                            await createChangeRequest({
                                space,
                                source: 'space-empty-state',
                            });
                        }}
                    />
                </EmptyStateActions>
            ) : null}
        </EmptyState>
    );
}
